// Area access grid — every page gets No access / View / Edit / Manage. Used by presets and per-person access.
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import AbilitiesEditor from "./AbilitiesEditor";

export const LEVEL_OPTS = [["none", "No access"], ["view", "View"], ["edit", "Edit"], ["manage", "Manage"]];
export const LEVEL_HINT = {
  none: "Page hidden",
  view: "Can open and read, no save buttons",
  edit: "Can create and edit",
  manage: "Edit plus delete, bulk changes and settings",
};
const tone = { none: "bg-white/[0.06] text-slate-300 border-white/20", view: "bg-sky-500/15 text-sky-200 border-sky-400/50", edit: "bg-orange-500/15 text-orange-200 border-orange-400/50", manage: "bg-emerald-500/15 text-emerald-200 border-emerald-400/50" };

function Seg({ value, onPick, disabled, testid, mixed }) {
  return (
    <span className="inline-flex shrink-0 rounded-md overflow-hidden border border-white/10" data-testid={testid}>
      {LEVEL_OPTS.map(([k, l]) => (
        <button key={k} type="button" disabled={disabled} title={LEVEL_HINT[k]} onClick={() => onPick(k)}
          className={`px-2 py-1 text-[10.5px] font-medium border-l first:border-l-0 transition-colors disabled:cursor-not-allowed ${value === k && !mixed ? tone[k] : "border-white/10 text-slate-500 hover:text-white"}`}
          data-testid={`${testid}-${k}`}>{l}</button>
      ))}
    </span>
  );
}

// value/base: {page_id: "view"|"edit"|"manage"}; missing = no access. base=null → preset mode (no custom marks).
export default function LevelGrid({ groups, value, base, onChange, locked, abilities }) {
  const [open, setOpen] = useState(() => new Set(groups.map((g) => g.page_id)));
  const lvl = (id) => value[id] || "none";
  const setMany = (ids, l) => { const n = { ...value }; ids.forEach((id) => { if (l === "none") delete n[id]; else n[id] = l; }); onChange(n); };
  return (
    <div className="space-y-2" data-testid="level-grid">
      {groups.map((sec) => {
        const ids = sec.children.map((c) => c.page_id);
        const set = new Set(ids.map(lvl));
        const on = ids.filter((id) => lvl(id) !== "none").length;
        const isOpen = open.has(sec.page_id);
        const custom = base && ids.some((id) => lvl(id) !== (base[id] || "none"));
        return (
          <div key={sec.page_id} className="rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`level-section-${sec.page_id}`}>
            <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.03]">
              <button type="button" onClick={() => setOpen((p) => { const n = new Set(p); n.has(sec.page_id) ? n.delete(sec.page_id) : n.add(sec.page_id); return n; })}
                className="p-0.5 rounded hover:bg-white/10 text-slate-300" data-testid={`level-section-toggle-${sec.page_id}`}>
                <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
              </button>
              <span className="text-sm font-semibold text-white flex-1 min-w-0 truncate">{sec.label}
                <span className="ml-2 text-[10.5px] font-mono text-slate-400">{on}/{ids.length}</span>
                {custom && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-fuchsia-300">custom</span>}
              </span>
              {ids.length > 0 && <Seg value={[...set][0]} mixed={set.size > 1} disabled={locked} onPick={(l) => setMany(ids, l)} testid={`level-all-${sec.page_id}`} />}
            </div>
            {isOpen && (
              <div className="divide-y divide-white/5">
                {sec.children.map((c) => {
                  const l = lvl(c.page_id);
                  const diff = base && l !== (base[c.page_id] || "none");
                  return (
                    <div key={c.page_id} className="flex items-center gap-2 pl-9 pr-3 py-1.5" data-testid={`level-row-${c.page_id}`}>
                      <span className={`text-[13px] flex-1 min-w-0 truncate ${l === "none" ? "text-slate-500" : "text-slate-100"}`}>
                        {c.label}
                        {c.admin_only && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-rose-300/70">admin</span>}
                        {diff && <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-fuchsia-300" title={`Preset: ${base[c.page_id] || "none"}`} data-testid={`level-custom-${c.page_id}`}>custom</span>}
                      </span>
                      <Seg value={l} disabled={locked} onPick={(k) => setMany([c.page_id], k)} testid={`level-${c.page_id}`} />
                    </div>
                  );
                })}
                {abilities && <AbilitiesEditor {...abilities} area={sec.page_id} testid={`${abilities.testid}-${sec.page_id}`} />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
